import { useEffect, useRef, useState } from "react";
import { Camera, RefreshCw, SwitchCamera, Upload } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const useCamera = (enabled, facing) => {
  const [stream, setStream] = useState(null);
  const [error, setError] = useState("");
  useEffect(() => {
    if (!enabled) return undefined;
    let s = null;
    let cancelled = false;
    setError("");
    if (!navigator.mediaDevices?.getUserMedia) { setError("Kamera tidak tersedia di perangkat ini."); return undefined; }
    navigator.mediaDevices.getUserMedia({ video: { facingMode: facing }, audio: false })
      .then((st) => { if (cancelled) { st.getTracks().forEach((t) => t.stop()); return; } s = st; setStream(st); })
      .catch(() => setError("Izin kamera ditolak atau kamera tidak ditemukan. Gunakan unggah foto."));
    return () => { cancelled = true; s?.getTracks().forEach((t) => t.stop()); setStream(null); };
  }, [enabled, facing]);
  return { stream, error };
};

const stampImage = (canvas) => {
  const g = window.__gps;
  const ctx = canvas.getContext("2d");
  const t = new Date().toLocaleString("id-ID", { timeZone: "Asia/Jakarta", day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit", second: "2-digit" }) + " WIB";
  const lines = [t, g ? `GPS ${g.lat.toFixed(6)}, ${g.lng.toFixed(6)}` : "GPS tidak tersedia"];
  const fs = Math.max(16, Math.round(canvas.width / 32));
  ctx.font = `600 ${fs}px sans-serif`;
  const h = fs * 1.4 * lines.length + fs;
  ctx.fillStyle = "rgba(0,0,0,0.55)";
  ctx.fillRect(0, canvas.height - h, canvas.width, h);
  ctx.fillStyle = "#fff";
  lines.forEach((l, i) => ctx.fillText(l, fs * 0.7, canvas.height - h + fs * 1.3 * (i + 1)));
};

export const CameraCapture = ({ open, onOpenChange, onConfirm, busy, title = "Foto Anggota", description = "Ambil foto anggota sebagai bukti sebelum pencairan.", initialFacing = "user", frame = "face", stamp = false, lockFacing = false }) => {
  const videoRef = useRef(null);
  const [facing, setFacing] = useState(initialFacing);
  const [shot, setShot] = useState(null);
  const { stream, error } = useCamera(open && !shot, facing);

  useEffect(() => {
    if (videoRef.current && stream) videoRef.current.srcObject = stream;
  }, [stream, shot]);
  useEffect(() => {
    if (!open) setShot(null);
  }, [open]);

  const capture = () => {
    const v = videoRef.current;
    if (!v || !v.videoWidth) return;
    const c = document.createElement("canvas");
    c.width = v.videoWidth; c.height = v.videoHeight;
    c.getContext("2d").drawImage(v, 0, 0);
    if (stamp) stampImage(c);
    c.toBlob((b) => setShot({ blob: b, url: URL.createObjectURL(b) }), "image/jpeg", 0.85);
  };
  const onFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    const img = new Image();
    const src = URL.createObjectURL(f);
    img.onload = () => {
      const c = document.createElement("canvas");
      const scale = Math.min(1, 1600 / Math.max(img.width, img.height));
      c.width = img.width * scale; c.height = img.height * scale;
      c.getContext("2d").drawImage(img, 0, 0, c.width, c.height);
      if (stamp) stampImage(c);
      URL.revokeObjectURL(src);
      c.toBlob((b) => setShot({ blob: b, url: URL.createObjectURL(b) }), "image/jpeg", 0.85);
    };
    img.onerror = () => { URL.revokeObjectURL(src); toast.error("Format foto tidak didukung, gunakan JPG/PNG"); };
    img.src = src;
    e.target.value = "";
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg" data-testid="camera-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading">{title}</DialogTitle>
          <DialogDescription>{description}</DialogDescription>
        </DialogHeader>
        <div className="relative aspect-[4/3] rounded-2xl overflow-hidden bg-black grid place-items-center">
          {shot ? <img src={shot.url} alt="Foto anggota" className="h-full w-full object-cover" data-testid="camera-preview" />
            : stream ? <video ref={videoRef} autoPlay playsInline muted className="h-full w-full object-cover" data-testid="camera-video" />
              : <p className="text-sm text-white/70 px-6 text-center" data-testid="camera-error">{error || "Menyalakan kamera..."}</p>}
          {!shot && stream && <div className={frame === "card" ? "absolute inset-x-6 inset-y-10 rounded-2xl border-2 border-white/60 pointer-events-none" : "absolute inset-6 rounded-[40%] border-2 border-white/40 pointer-events-none"} />}
        </div>
        <div className="flex flex-wrap gap-2">
          {shot ? (
            <>
              <Button variant="outline" className="h-11 rounded-full flex-1" onClick={() => setShot(null)} data-testid="camera-retake"><RefreshCw className="h-4 w-4 mr-2" />Ulangi</Button>
              <Button className="h-11 rounded-full flex-1" disabled={busy} onClick={() => onConfirm(shot.blob)} data-testid="camera-confirm">{busy ? "Mengunggah..." : "Gunakan Foto"}</Button>
            </>
          ) : (
            <>
              <Button className="h-12 rounded-full flex-1 pulse-glow" disabled={!stream} onClick={capture} data-testid="camera-capture"><Camera className="h-5 w-5 mr-2" />Ambil Foto</Button>
              {!lockFacing && <Button variant="outline" className="h-12 w-12 rounded-full p-0" onClick={() => setFacing((f) => (f === "user" ? "environment" : "user"))} aria-label="Ganti kamera" data-testid="camera-switch"><SwitchCamera className="h-5 w-5" /></Button>}
              <label className="h-12 rounded-full border px-4 inline-flex items-center text-sm font-medium cursor-pointer hover:bg-muted" data-testid="camera-upload-label">
                <Upload className="h-4 w-4 mr-2" />Unggah
                <input type="file" accept="image/*" capture={facing === "user" ? "user" : "environment"} className="hidden" onChange={onFile} data-testid="camera-file-input" />
              </label>
            </>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};
